/*
 This file is part of GNU Taler
 (C) 2021-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  HttpStatusCode,
  Paytos,
  TalerError,
  TalerMerchantApi,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  useCommonPreferences,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { ErrorLoadingMerchant } from "../../../../components/ErrorLoadingMerchant.js";
import { Loading } from "../../../../components/exception/loading.js";
import {
  ConfirmModal,
  ValidBankAccount,
} from "../../../../components/modal/index.js";
import { useInstanceKYCDetails } from "../../../../hooks/instance.js";
import { ListPage } from "./ListPage.js";
import { useEffect } from "preact/hooks";

const TALER_SCREEN_ID = 40;

interface Props {
  // onGetInfo: (id: string) => void;
  // onShowInstructions: (id: string) => void;
}

export default function ListKYC(_p: Props): VNode {
  const result = useInstanceKYCDetails();

  const [showingInstructions, setShowingInstructions] = useState<
    TalerMerchantApi.MerchantAccountKycRedirect | undefined
  >(undefined);

  // useEffect(() => {
  //   if (!result || result instanceof TalerError || result.type === "fail") return;
  //   if (!result.body.kyc_data.length) return;
  //   setShowingInstructions(result.body.kyc_data[1])
  // })
  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  /**
   * This component just render known kyc requirements.
   * If query fail then is safe to hide errors.
   */
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.GatewayTimeout: {
        return <div />;
      }
      case HttpStatusCode.ServiceUnavailable: {
        return <div />;
      }
      case HttpStatusCode.Unauthorized: {
        return <div />;
      }
      case HttpStatusCode.NotFound: {
        return <div />;
      }
      case HttpStatusCode.NoContent: {
        return <div>no kyc required</div>;
      }
      case HttpStatusCode.NotModified: {
        return <div />;
      }
      default: {
        assertUnreachable(result);
      }
    }
  }
  const status = result.body;

  return (
    <Fragment>
      {showingInstructions !== undefined ? (
        <ShowInstructionForKycRedirect
          e={showingInstructions}
          onCancel={() => setShowingInstructions(undefined)}
        />
      ) : undefined}
      <ListPage
        status={status}
        // onGetInfo={async (exchange, ac) => {
        //   new URL()
        // }}
        onShowInstructions={(e) => {
          setShowingInstructions(e);
        }}
      />
    </Fragment>
  );
}

function ShowInstructionForKycRedirect({
  e,
  onCancel,
}: {
  e: TalerMerchantApi.MerchantAccountKycRedirect;
  onCancel: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const [{ showDebugInfo }] = useCommonPreferences();
  switch (e.status) {
    case TalerMerchantApi.MerchantAccountKycStatus.KYC_WIRE_REQUIRED:
      const uri = Paytos.fromString(e.payto_uri);
      if (uri.tag === "error") {
        return (
          <ConfirmModal
            label={i18n.str`Ok`}
            description={i18n.str`The account for wire transfers is invalid.`}
            active
            onCancel={onCancel}
          >
            {!showDebugInfo ? undefined : (
              <pre>
                {JSON.stringify({ strPayto: e.payto_uri, uri }, undefined, 2)}
              </pre>
            )}
            <p style={{ paddingTop: 0 }}>
              <i18n.Translate>
                The backend service responded with "{e.payto_uri}" which is
                invalid.
              </i18n.Translate>
            </p>
          </ConfirmModal>
        );
      }
      const tgs = (e.payto_kycauths ?? [])
        .map((d) => Paytos.fromString(d))
        .filter((d) => {
          if (d.tag !== "ok") {
            // FIXME: maybe this need to be shown to the user more promptly
            console.error("The server replied with a wrong payto:// URI", d);
          }
          return d.tag === "ok";
        })
        .map((d) => d.value);
      return (
        <ValidBankAccount
          origin={uri.value}
          targets={tgs}
          onCancel={onCancel}
          currency={e.exchange_currency}
        />
      );
    case TalerMerchantApi.MerchantAccountKycStatus.NO_EXCHANGE_KEY: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`No exchange keys`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The backend service is still synchronizing with the payment
              service provider.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.KYC_WIRE_IMPOSSIBLE: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`No transfers can be made from this account`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The core banking system cannot perform wire transfers between the
              payment service provider's accounts and your bank accounts. Thus
              you cannot use this payment service provider. Sincerely, the Taler
              Exchange.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }

    case TalerMerchantApi.MerchantAccountKycStatus.KYC_REQUIRED: {
      return (
        <ConfirmModal
          label={i18n.str`Test`}
          description={i18n.str`Know Your Customer process is required.`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The payment service provider requires more information.
            </i18n.Translate>{" "}
            {e.access_token === undefined ? undefined : (
              <a
                href={`${e.exchange_url}kyc-spa/${e.access_token}`}
                target="_blank"
                rel="noreferrer"
              >
                <i18n.Translate>
                  Click here to complete this step.
                </i18n.Translate>
              </a>
            )}
          </p>
          {e.access_token !== undefined ? undefined : (
            <p style={{ paddingTop: 0 }}>
              <i18n.Translate>
                We are waiting for the access token to be present. Check again
                later.
              </i18n.Translate>
            </p>
          )}
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.AWAITING_AML_REVIEW: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Awaiting AML review`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              This account cannot be used. The payment service provider must
              verify the account information manually.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.READY: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Ready`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              This account has been successfully configured for use with the
              payment service provider.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.LOGIC_BUG: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Logic bug`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The backend service detected an internal error, contact the system
              administrator or check again later.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.EXCHANGE_INTERNAL_ERROR: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Internal error`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The payment service provider detected an internal error, contact
              the system administrator or check again later.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.MERCHANT_INTERNAL_ERROR: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Internal error`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The merchant service provider detected an internal error, contact
              the system administrator or check again later.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.EXCHANGE_GATEWAY_TIMEOUT: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Logic bug`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              The backend service could not contact the payment service provider
              due to a timeout, contact the system administrator or check again
              later.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.EXCHANGE_UNREACHABLE: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Logic bug`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              Unable to reach the payment service provider, contact the system
              administrator or check again later.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.UNSUPPORTED_ACCOUNT: {
      return (
        <ConfirmModal
          label={i18n.str`Ok`}
          description={i18n.str`Unsupported account`}
          active
          onCancel={onCancel}
        >
          {!showDebugInfo ? undefined : (
            <pre>{JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}</pre>
          )}
          <p style={{ paddingTop: 0 }}>
            <i18n.Translate>
              This exchange does not support the given account.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      );
    }
    case TalerMerchantApi.MerchantAccountKycStatus.EXCHANGE_STATUS_INVALID:
      {
        return (
          <ConfirmModal
            label={i18n.str`Ok`}
            description={i18n.str`Logic bug`}
            active
            onCancel={onCancel}
          >
            {!showDebugInfo ? undefined : (
              <pre>
                {JSON.stringify({ strPayto: e.payto_uri }, undefined, 2)}
              </pre>
            )}
            <p style={{ paddingTop: 0 }}>
              <i18n.Translate>
                The payment service provider replied with an invalid status,
                contact the system administrator or check again later.
              </i18n.Translate>
            </p>
          </ConfirmModal>
        );
      }
      return <div />;
    default:
      assertUnreachable(e.status);
  }
}
